import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { languages, languageCodes, defaultLanguage } from '../i18n';
import { Globe, Check, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';

// Flag images from CDN
const flagImages = {
  en: 'https://flagcdn.com/w40/gb.png',
  uk: 'https://flagcdn.com/w40/ua.png',
  zh: 'https://flagcdn.com/w40/cn.png',
  es: 'https://flagcdn.com/w40/es.png',
  ru: 'https://flagcdn.com/w40/ru.png',
  de: 'https://flagcdn.com/w40/de.png',
  fr: 'https://flagcdn.com/w40/fr.png',
  ja: 'https://flagcdn.com/w40/jp.png',
  th: 'https://flagcdn.com/w40/th.png',
  hi: 'https://flagcdn.com/w40/in.png',
};

export default function LanguageSwitcher({ variant = 'default' }) {
  const { i18n, t } = useTranslation();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  // Get current path without language prefix
  const getPathWithoutLang = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts[0] && languageCodes.includes(pathParts[0])) {
      return '/' + pathParts.slice(1).join('/') || '/';
    }
    return location.pathname;
  };

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    
    // Navigate to the new language URL
    const currentPath = getPathWithoutLang();
    let newPath;
    
    if (langCode === defaultLanguage) {
      // English (default) - no prefix
      newPath = currentPath || '/';
    } else {
      // Other languages - add prefix
      newPath = `/${langCode}${currentPath === '/' ? '' : currentPath}`;
    }
    
    navigate(newPath + location.search);
  };

  // Prominent variant for footer - more visible with full language name
  if (variant === 'prominent') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className={cn(
              "gap-2 px-4 py-2 h-auto",
              isDark 
                ? "bg-gray-800/50 border-gray-700 hover:bg-gray-700/50 hover:border-purple-500/50 text-gray-300 hover:text-white" 
                : "bg-white border-gray-300 hover:bg-gray-50 hover:border-purple-400 text-gray-700"
            )}
            data-testid="language-switcher-prominent"
          >
            <Globe className="h-4 w-4 text-purple-400" />
            <img 
              src={flagImages[currentLang.code]} 
              alt={currentLang.name}
              className="w-5 h-auto rounded-sm"
            />
            <span className="font-medium">{currentLang.name}</span>
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="start" 
          className={cn(
            "min-w-[220px] max-h-[320px] overflow-y-auto",
            isDark ? "bg-[#1a1a2e] border-gray-700" : "bg-white border-gray-200"
          )}
        >
          <div className={cn("px-3 py-2 text-xs font-semibold uppercase tracking-wider border-b", isDark ? "text-gray-500 border-gray-700" : "text-gray-400 border-gray-200")}>
            {t('settings.language', 'Select Language')}
          </div>
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              data-testid={`lang-option-${lang.code}`}
              className={cn(
                "cursor-pointer flex items-center gap-3 py-2.5 px-3",
                isDark ? "text-gray-300 hover:text-white hover:bg-gray-700/50" : "text-gray-700 hover:text-gray-900 hover:bg-gray-100",
                i18n.language === lang.code && (isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-50 text-purple-600')
              )}
            >
              <img 
                src={flagImages[lang.code]} 
                alt={lang.name}
                className="w-6 h-auto rounded-sm shadow-sm"
              />
              <span className="flex-1 font-medium">{lang.name}</span>
              {i18n.language === lang.code && (
                <Check className="h-4 w-4 text-purple-500" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default compact variant
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="gap-2 text-gray-300 hover:text-white px-2"
          data-testid="language-switcher"
        >
          <img 
            src={flagImages[currentLang.code]} 
            alt={currentLang.name}
            className="w-5 h-auto rounded-sm"
          />
          <span className="hidden sm:inline text-sm font-medium">{currentLang.code.toUpperCase()}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#1a1a2e] border-gray-700 min-w-[200px]">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            data-testid={`lang-option-${lang.code}`}
            className={`cursor-pointer flex items-center gap-3 py-2.5 px-3 text-gray-300 hover:text-white hover:bg-gray-700/50 ${
              i18n.language === lang.code ? 'bg-orange-500/20 text-orange-400' : ''
            }`}
          >
            <img 
              src={flagImages[lang.code]} 
              alt={lang.name}
              className="w-6 h-auto rounded-sm"
            />
            <span className="flex-1">{lang.name}</span>
            {i18n.language === lang.code && (
              <Check className="h-4 w-4 text-orange-400" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
